/**
 * functions common util
 *
 * @category   Javascript
 * @author     Jakob Ohlsen <jakob.ohlsen@gmail.com>
 * @copyright  2025 Jakob Ohlsen
 */
// Internal — synchronous clipboard write through a transient textarea and the
// deprecated `execCommand('copy')`. Every browser still implements it and it is
// the only path available in insecure contexts (http dev hosts) or when the
// async Clipboard API refuses the write. It has to run inside the task of the
// user gesture: Firefox rejects `execCommand('copy')` once the call has left the
// click handler.
function fallbackCopyTextToClipboard(text) {
    const previouslyFocused = document.activeElement;
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.setAttribute('readonly', '');
    // Park it off-screen, but keep it rendered — `display: none` or
    // `visibility: hidden` would make the selection impossible. `position: fixed`
    // at the viewport origin also avoids scrolling the page to it.
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.opacity = '0';
    textArea.style.pointerEvents = 'none';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    textArea.setSelectionRange(0, text.length);
    let successful = false;
    try {
        successful = document.execCommand('copy');
    }
    catch {
        successful = false;
    }
    document.body.removeChild(textArea);
    if (previouslyFocused instanceof HTMLElement) {
        previouslyFocused.focus();
    }
    return successful;
}
/**
 * Copies `text` to the clipboard and resolves to whether it worked.
 *
 * `navigator.clipboard` only exists in secure contexts (https or localhost), and
 * Firefox additionally rejects `writeText()` whenever it does not consider the
 * write covered by a user gesture — so the async API is tried first and the
 * legacy textarea path covers everything it cannot do. Call this straight from a
 * click handler: both paths rely on the user activation of that event.
 */
export async function copyTextToClipboard(text) {
    if (navigator.clipboard && window.isSecureContext) {
        try {
            await navigator.clipboard.writeText(text);
            return true;
        }
        catch {
            // Blocked or unsupported — fall through to the legacy path.
        }
    }
    return fallbackCopyTextToClipboard(text);
}
